import { ContactFormData } from './formHandler';
import emailjs from '@emailjs/browser';

// Configuración de EmailJS
const EMAILJS_CONFIG = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_openweb_studio',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_openweb_contact',
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY'
};

// Endpoint de Formspree como respaldo
const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT || 'https://formspree.io/f/xkgzvnrk';

export class RealEmailService {
  
  // Función principal para enviar email
  static async sendEmail(data: ContactFormData): Promise<{ success: boolean; message: string }> {
    try {
      console.log('📧 Intentando enviar email con EmailJS...');
      
      // Intentar con EmailJS primero
      const emailjsResult = await this.sendWithEmailJS(data);
      if (emailjsResult.success) {
        return emailjsResult;
      }
      
      console.log('⚠️ EmailJS falló, intentando con Formspree...');
      
      // Si EmailJS falla, intentar con Formspree
      const formspreeResult = await this.sendWithFormspree(data);
      if (formspreeResult.success) {
        return formspreeResult;
      }
      
      console.log('⚠️ Ambos servicios fallaron, usando simulación...');
      
      // Si ambos fallan, usar simulación
      return await this.sendEmailSimulation(data);
      
    } catch (error) {
      console.error('Error general al enviar email:', error);
      return await this.sendEmailSimulation(data);
    }
  }

  // Enviar usando EmailJS
  private static async sendWithEmailJS(data: ContactFormData): Promise<{ success: boolean; message: string }> {
    try {
      // Verificar si EmailJS está configurado
      if (!EMAILJS_CONFIG.serviceId || EMAILJS_CONFIG.serviceId === 'service_openweb_studio') {
        throw new Error('EmailJS no configurado');
      }

      const templateParams = {
        to_email: 'openwebstudios@gmail.com',
        from_name: `${data.firstName} ${data.lastName}`,
        from_email: data.email,
        phone: data.phone || 'No proporcionado',
        message: data.message,
        subject: `Nuevo mensaje de ${data.firstName} ${data.lastName}`
      };

      const result = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        templateParams,
        EMAILJS_CONFIG.publicKey
      );

      if (result.status === 200) {
        console.log('✅ Email enviado exitosamente con EmailJS');
        return {
          success: true,
          message: '¡Mensaje enviado exitosamente! Te contactaremos pronto.'
        };
      } else {
        throw new Error('EmailJS falló');
      }
    } catch (error) {
      console.log('⚠️ Error con EmailJS:', error);
      return { success: false, message: 'EmailJS no disponible' };
    }
  }

  // Enviar usando Formspree
  private static async sendWithFormspree(data: ContactFormData): Promise<{ success: boolean; message: string }> {
    try {
      const formData = new FormData();
      formData.append('name', `${data.firstName} ${data.lastName}`);
      formData.append('email', data.email);
      formData.append('phone', data.phone || 'No proporcionado');
      formData.append('message', data.message);
      formData.append('subject', 'Nuevo mensaje de OpenWeb Studio');
      formData.append('_replyto', data.email);
      formData.append('_subject', `Nuevo mensaje de ${data.firstName} ${data.lastName}`);

      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ Email enviado exitosamente con Formspree');
        return {
          success: true,
          message: '¡Mensaje enviado exitosamente! Te contactaremos pronto.'
        };
      } else {
        throw new Error('Formspree falló');
      }
    } catch (error) {
      console.log('⚠️ Error con Formspree:', error);
      return { success: false, message: 'Formspree no disponible' };
    }
  }

  // Simulación de envío para desarrollo
  private static async sendEmailSimulation(data: ContactFormData): Promise<{ success: boolean; message: string }> {
    try {
      console.log('📧 Simulando envío de email...');
      
      // Simular delay de envío
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('📧 Email simulado enviado:', {
        to: 'openwebstudios@gmail.com',
        from: 'ramiromunoz@myportfolio-rm.com',
        subject: `Nuevo mensaje de ${data.firstName} ${data.lastName}`,
        body: `
          Nombre: ${data.firstName} ${data.lastName}
          Email: ${data.email}
          Teléfono: ${data.phone || 'No proporcionado'}
          Mensaje: ${data.message}
        `
      });

      return {
        success: true,
        message: '¡Mensaje enviado exitosamente! Te contactaremos pronto. (Modo simulación - Para emails reales, configura EmailJS o Formspree)'
      };
    } catch (error) {
      console.error('Error en simulación:', error);
      return {
        success: false,
        message: 'Error al enviar el mensaje. Por favor, intenta nuevamente.'
      };
    }
  }

  // Función para configurar servicios automáticamente
  static async setupServices(): Promise<void> {
    try {
      console.log('🔧 Configurando servicios de email...');
      
      // Aquí podrías hacer configuración automática
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('✅ Servicios configurados');
      console.log('📝 Para emails reales:');
      console.log('1. Configura EmailJS en emailjs.com');
      console.log('2. O crea un endpoint en formspree.io');
    } catch (error) {
      console.log('⚠️ No se pudieron configurar los servicios automáticamente');
    }
  }
} 